import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography} from '@mui/material';
import {get_currency_mark, get_price_str} from 'common';
import {useMemo} from 'react';
import {IOrder} from 'types';

export default function OrderDetailDialog(props: {open: boolean; order: IOrder | null; onClose: () => void}) {
	const {open, order, onClose} = props;

	const pricing = useMemo(() => {
		if (!order) return null;
		const currency = get_currency_mark(order.exchange);
		const formatPrice = (value: number) => `${currency} ${get_price_str(value, true)}`;
		return {
			seed: formatPrice(order.order_price * order.volume),
			orderPrice: formatPrice(order.order_price),
			closePrice: formatPrice(order.close_price ?? 0),
			profit: `${currency} ${get_price_str(order.profit, false, 0)}`,
		};
	}, [order]);

	if (!order) return null;

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>Order Details</DialogTitle>
			<DialogContent dividers>
				<Stack spacing={2}>
					<Stack spacing={0.5}>
						<Typography variant="overline" color="text.secondary">
							Identifiers
						</Typography>
						<Stack spacing={0.25}>
							<DataRow label="Order ID" value={order.order_id} />
							<DataRow label="Exchange" value={order.exchange} />
							<DataRow label="Symbol" value={order.ticker} />
						</Stack>
					</Stack>

					<Divider />

					<Stack spacing={0.5}>
						<Typography variant="overline" color="text.secondary">
							Position
						</Typography>
						<Stack spacing={0.25}>
							<DataRow label="Position" value={order.position ?? 'long'} />
							<DataRow label="Direction" value={order.direction} />
							<DataRow label="Volume" value={order.volume.toString()} />
						</Stack>
					</Stack>

					<Divider />

					<Stack spacing={0.5}>
						<Typography variant="overline" color="text.secondary">
							Pricing
						</Typography>
						<Stack spacing={0.25}>
							<DataRow label="Seed" value={pricing?.seed ?? '-'} />
							<DataRow label="Entry" value={pricing?.orderPrice ?? '-'} />
							<DataRow label="Exit" value={pricing?.closePrice ?? '-'} />
							<DataRow label="Profit" value={pricing?.profit ?? '-'} />
						</Stack>
					</Stack>

					<Divider />

					<Stack spacing={0.5}>
						<Typography variant="overline" color="text.secondary">
							Timeline
						</Typography>
						<Stack spacing={0.25}>
							<DataRow label="Opened" value={new Date(order.timestamp * 1000).toLocaleString()} />
							<DataRow label="State" value={order.state} />
						</Stack>
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Close</Button>
			</DialogActions>
		</Dialog>
	);
}

function DataRow(props: {label: string; value: string}) {
	const {label, value} = props;
	return (
		<Stack direction="row" justifyContent="space-between" alignItems="center">
			<Typography variant="body2" color="text.secondary">
				{label}
			</Typography>
			<Typography variant="body2" fontWeight={500}>
				{value}
			</Typography>
		</Stack>
	);
}
